import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Lightweight URL filter hook.
 * Responsibilities:
 * - Each filter key is (optionally) prefixed and stored as an individual query parameter.
 * - Empty / undefined values are stripped from the URL (keeps it clean & shareable).
 * - Can atomically reset a page parameter (e.g. "page") when filters change to avoid double URL updates.
 * - Keeps local state in sync with browser back/forward navigation.
 */
export interface UseUrlFiltersOptions<T extends Record<string, any>> {
  keys: (keyof T)[];
  defaultValues?: Partial<T>;
  paramPrefix?: string; // optional prefix for each individual filter query param (avoid collisions per module)
  resetPageParam?: string; // optional page param name to delete atomically when filters change
}

export const useUrlFilters = <T extends Record<string, any>>({
  keys,
  defaultValues = {},
  paramPrefix = "",
  resetPageParam,
}: UseUrlFiltersOptions<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initial load from URL (one-time during mount)
  const [filters, setFiltersState] = useState<Partial<T>>(() => {
    const initial: Partial<T> = { ...defaultValues };
    keys.forEach((k) => {
      const val = searchParams.get(paramPrefix + String(k));
      if (val != null && val !== "") initial[k] = val as any;
    });
    return initial;
  });

  // Sync on URL changes (back/forward navigation)
  useEffect(() => {
    const next: Partial<T> = { ...defaultValues };
    keys.forEach((k) => {
      const val = searchParams.get(paramPrefix + String(k));
      if (val != null && val !== "") next[k] = val as any;
    });
    const changed =
      Object.keys(next).length !== Object.keys(filters).length ||
      keys.some((k) => next[k] !== filters[k]);
    if (changed) setFiltersState(next);
  }, [defaultValues, keys, paramPrefix, searchParams, filters]);

  const setFilters = useCallback(
    (next: Partial<T>) => {
      // Build a cleaned subset containing only non-empty values
      const cleaned: Partial<T> = {};
      keys.forEach((k) => {
        const v = next[k];
        if (v != null && v !== "") cleaned[k] = v;
      });
      setFiltersState(cleaned);

      const newParams = new URLSearchParams(searchParams.toString());

      // Remove previously managed keys (avoid stale params)
      keys.forEach((k) => newParams.delete(paramPrefix + String(k)));

      // Add cleaned filter params
      Object.entries(cleaned).forEach(([k, v]) => {
        newParams.set(paramPrefix + k, String(v));
      });

      // Reset page param atomically so pagination state matches new filter set
      if (resetPageParam) newParams.delete(resetPageParam);
      setSearchParams(newParams, { replace: true });
    },
    [keys, paramPrefix, resetPageParam, searchParams, setSearchParams],
  );

  return { filters, setFilters } as const;
};
